import { useTranslation } from "react-i18next";
import logo from "../assets/spotto-logo.png";
import solidLabsLogo from "../assets/solidlabs-logo-transparent.png";
import { SpottoHeader } from "./common/SpottoHeader";
import screen1 from "../assets/screenshots/01-home.png";
import screen2 from "../assets/screenshots/02-explore.png";
import screen3 from "../assets/screenshots/03-booking.png";
import screen4 from "../assets/screenshots/04-training.png";
import screen5 from "../assets/screenshots/05-profile.png";

const BUSINESS_APP_URL = "https://business.spotto.pl";
const CORPORATE_URL = "https://solidlabs.site";
const IOS_APP_URL = "https://apps.apple.com/pl/app/spotto/id6759284774?l=pl";
const ANDROID_APP_URL = "https://play.google.com/store/apps/details?id=pl.spotto.mobile";

function qrUrl(target: string) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=${encodeURIComponent(target)}`;
}

function AppBadge({ store, label, href }: { store: "apple" | "google"; label: string; href?: string }) {
  const content = (
    <>
      <span className="text-2xl text-ink-on-dark" aria-hidden>
        {store === "apple" ? "" : "▶"}
      </span>
      <div className="text-left leading-tight">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-on-dark-muted">
          {label.split("|")[0]}
        </div>
        <div className="text-sm font-semibold text-ink-on-dark">
          {label.split("|")[1]}
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white/[0.06] border border-white/[0.12] hover:bg-white/[0.10] hover:border-white/[0.20] transition select-none"
        title={label}
      >
        {content}
      </a>
    );
  }

  return (
    <div
      className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] opacity-70 cursor-not-allowed select-none"
      title={label}
    >
      {content}
    </div>
  );
}

export function Landing() {
  const { t } = useTranslation();

  const screenshots = [
    { src: screen1, caption: t("landing.screenshots.caption1") },
    { src: screen2, caption: t("landing.screenshots.caption2") },
    { src: screen3, caption: t("landing.screenshots.caption3") },
    { src: screen4, caption: t("landing.screenshots.caption4") },
    { src: screen5, caption: t("landing.screenshots.caption5") },
  ];

  const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    className: "w-6 h-6",
  };

  const features = [
    {
      key: "sports",
      color: "#c06f57",
      icon: (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5.5" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      title: t("landing.features.items.sports.title"),
      description: t("landing.features.items.sports.description"),
    },
    {
      key: "map",
      color: "#4d63ac",
      icon: (
        <svg {...iconProps}>
          <path d="M12 21s-7-7.5-7-12a7 7 0 1 1 14 0c0 4.5-7 12-7 12z" />
          <circle cx="12" cy="9" r="2.6" />
        </svg>
      ),
      title: t("landing.features.items.map.title"),
      description: t("landing.features.items.map.description"),
    },
    {
      key: "booking",
      color: "#5fa78a",
      icon: (
        <svg {...iconProps}>
          <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
          <path d="M3.5 10h17M8 3v4M16 3v4" />
          <path d="M9 14.5l2 2 4-4" />
        </svg>
      ),
      title: t("landing.features.items.booking.title"),
      description: t("landing.features.items.booking.description"),
    },
    {
      key: "trainings",
      color: "#ff5622",
      icon: (
        <svg {...iconProps}>
          <path d="M13 2.5L5 13.5h6l-1 8 8-11h-6l1-8z" />
        </svg>
      ),
      title: t("landing.features.items.trainings.title"),
      description: t("landing.features.items.trainings.description"),
    },
    {
      key: "products",
      color: "#006a7a",
      icon: (
        <svg {...iconProps}>
          <path d="M3 8.5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2a2.5 2.5 0 0 0 0 5v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2a2.5 2.5 0 0 0 0-5v-2z" />
          <path d="M9 8v9" strokeDasharray="2 2" />
        </svg>
      ),
      title: t("landing.features.items.products.title"),
      description: t("landing.features.items.products.description"),
    },
  ];

  const pillars = [
    {
      key: "facilities",
      color: "#4d63ac",
      icon: (
        <svg {...iconProps}>
          <rect x="5" y="3.5" width="14" height="17" rx="1.5" />
          <path d="M9 8h2M13 8h2M9 12h2M13 12h2" />
          <path d="M10.5 20.5v-4h3v4" />
        </svg>
      ),
      title: t("landing.partners.pillars.facilities.title"),
      items: t("landing.partners.pillars.facilities.items", { returnObjects: true }) as string[],
    },
    {
      key: "trainers",
      color: "#5fa78a",
      icon: (
        <svg {...iconProps}>
          <circle cx="9" cy="8.5" r="3" />
          <circle cx="16.5" cy="9.5" r="2.3" />
          <path d="M3 19.5a6 6 0 0 1 12 0" />
          <path d="M14 14a4.5 4.5 0 0 1 7 4.5" />
        </svg>
      ),
      title: t("landing.partners.pillars.trainers.title"),
      items: t("landing.partners.pillars.trainers.items", { returnObjects: true }) as string[],
    },
    {
      key: "sales",
      color: "#ff5622",
      icon: (
        <svg {...iconProps}>
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M3 10h18" />
          <path d="M7 15h3" />
        </svg>
      ),
      title: t("landing.partners.pillars.sales.title"),
      items: t("landing.partners.pillars.sales.items", { returnObjects: true }) as string[],
    },
    {
      key: "support",
      color: "#006a7a",
      icon: (
        <svg {...iconProps}>
          <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
          <path d="M8 16.5v-4M12 16.5v-7M16 16.5v-2.5" />
        </svg>
      ),
      title: t("landing.partners.pillars.support.title"),
      items: t("landing.partners.pillars.support.items", { returnObjects: true }) as string[],
    },
  ];

  return (
    <div className="relative isolate min-h-screen bg-surface-dark text-ink-on-dark overflow-x-clip pt-16">
      {/* Global decorative backgrounds */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-grid" aria-hidden />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-radial-top" aria-hidden />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-radial-bottom" aria-hidden />

      {/* Nav */}
      <SpottoHeader />

      {/* Hero */}
      <section className="relative max-w-6xl mx-auto px-6 min-h-[calc(100vh-4rem)] flex flex-col justify-center py-12">
        <div className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-brand-primary/[0.10] blur-[120px] -z-10" aria-hidden />
        <div className="relative grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-accent mb-5">
              {t("landing.hero.eyebrow")}
            </p>
            <h1 className="font-display font-light text-[clamp(2rem,4.5vw,3.75rem)] tracking-[-0.025em] leading-[1.05] mb-7 text-gradient-brand">
              {t("landing.hero.title")}
            </h1>
            <p className="text-lg text-ink-on-dark-muted max-w-xl mb-10 leading-relaxed">
              {t("landing.hero.subtitle")}
            </p>

            <div className="mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <AppBadge store="apple" label={`${t("landing.hero.downloadFrom")}|${t("landing.hero.appStore")}`} href={IOS_APP_URL} />
                <AppBadge store="google" label={`${t("landing.hero.downloadFrom")}|${t("landing.hero.googlePlay")}`} href={ANDROID_APP_URL} />
                <a
                  href="#partners"
                  className="text-sm font-medium text-brand-accent hover:text-brand-accent-hover transition px-2"
                >
                  {t("landing.hero.partnersCta")} →
                </a>
              </div>
            </div>
          </div>

          {/* Hero phone preview */}
          <div className="relative flex justify-center">
            <div className="relative">
              <img
                src={screen1}
                alt="Spotto"
                className="w-[175px] md:w-[200px] rounded-[2.5rem] border-[5px] border-white/[0.10] glow-box"
              />
              <div className="absolute -inset-4 -z-10 rounded-full bg-brand-primary/25 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 scroll-mt-16 min-h-[88vh] flex flex-col justify-center py-16">
        <div className="text-center mb-12">
          <h2 className="font-display font-light text-3xl md:text-4xl tracking-[-0.02em] mb-3">
            {t("landing.features.title")}
          </h2>
          <p className="text-ink-on-dark-muted max-w-2xl mx-auto">
            {t("landing.features.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5">
          {features.map((f) => (
            <div
              key={f.key}
              className="feature-card p-6 rounded-2xl border text-center"
              style={{ ["--card-color" as string]: f.color } as React.CSSProperties}
            >
              <div className="relative feature-icon-chip w-12 h-12 rounded-xl flex items-center justify-center mb-5 mx-auto" aria-hidden>
                {f.icon}
              </div>
              <h3 className="relative font-display text-lg font-semibold tracking-tight mb-2 text-ink-on-dark">{f.title}</h3>
              <p className="relative text-sm text-ink-on-dark-muted leading-relaxed">{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Screenshots */}
      <section className="border-y border-white/[0.06] bg-white/[0.02] min-h-[88vh] flex flex-col justify-center">
        <div className="max-w-6xl mx-auto px-6 py-16 w-full">
          <div className="text-center mb-12">
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-[-0.02em] mb-3">
              {t("landing.screenshots.title")}
            </h2>
            <p className="text-ink-on-dark-muted max-w-2xl mx-auto">
              {t("landing.screenshots.subtitle")}
            </p>
          </div>

          <div className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory -mx-6 px-6 md:grid md:grid-cols-5 md:gap-4 md:overflow-visible md:px-0 md:mx-auto md:max-w-[1000px]">
            {screenshots.map((s, idx) => (
              <figure key={idx} className="shrink-0 snap-center w-[180px] md:w-auto">
                <img
                  src={s.src}
                  alt={s.caption}
                  className="w-full rounded-3xl border-[6px] border-white/[0.10] shadow-lg"
                />
                <figcaption className="mt-3 text-center text-xs text-ink-on-dark-muted/70">
                  {s.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Get the app — QR codes */}
      <section id="get-the-app" className="max-w-6xl mx-auto px-6 min-h-[88vh] flex flex-col justify-center py-16">
        <div className="max-w-3xl mx-auto w-full">
          <div className="text-center mb-8">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-accent mb-3">
              {t("landing.appQr.eyebrow")}
            </p>
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-[-0.02em] mb-3">
              {t("landing.appQr.title")}
            </h2>
            <p className="text-ink-on-dark-muted max-w-2xl mx-auto">
              {t("landing.appQr.subtitle")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* iOS — live, full tile clickable */}
            <a
              href={IOS_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-5 p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-brand-primary/40 hover:bg-white/[0.05] transition group"
              aria-label={`${t("landing.appQr.title")} — ${t("landing.hero.appStore")}`}
            >
              <img
                src={qrUrl(IOS_APP_URL)}
                alt="QR App Store"
                className="w-28 h-28 rounded-lg bg-white p-2 shrink-0"
                width={112}
                height={112}
              />
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-primary mb-1">iOS</div>
                <div className="text-base font-semibold mb-1 text-ink-on-dark">{t("landing.hero.appStore")}</div>
                <div className="text-xs text-ink-on-dark-muted">
                  <span className="hidden sm:inline">{t("landing.appQr.scan")}</span>
                  <span className="sm:hidden">{t("landing.appQr.tapToInstall")}</span>
                </div>
              </div>
            </a>
            {/* Android — live, full tile clickable (works as scan target on desktop, direct download on mobile) */}
            <a
              href={ANDROID_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-5 p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-brand-primary/40 hover:bg-white/[0.05] transition group"
              aria-label={`${t("landing.appQr.title")} — ${t("landing.hero.googlePlay")}`}
            >
              <img
                src={qrUrl(ANDROID_APP_URL)}
                alt="QR Google Play"
                className="w-28 h-28 rounded-lg bg-white p-2 shrink-0"
                width={112}
                height={112}
              />
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-primary mb-1">Android</div>
                <div className="text-base font-semibold mb-1 text-ink-on-dark">{t("landing.hero.googlePlay")}</div>
                <div className="text-xs text-ink-on-dark-muted">
                  <span className="hidden sm:inline">{t("landing.appQr.scan")}</span>
                  <span className="sm:hidden">{t("landing.appQr.tapToInstall")}</span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* For partners — LIGHT SECTION */}
      <section
        id="partners"
        className="relative bg-surface-light text-ink-primary border-y border-border-light scroll-mt-16 min-h-[88vh] flex flex-col justify-center"
      >
        <div className="pointer-events-none absolute inset-0 bg-grid-light opacity-70" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6 py-8 w-full">
          <div className="text-center max-w-3xl mx-auto mb-6">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary mb-3">
              {t("landing.partners.eyebrow")}
            </p>
            <h2 className="font-display font-light text-3xl md:text-4xl tracking-[-0.025em] leading-[1.1] mb-3 text-gradient-brand-light">
              {t("landing.partners.title")}
            </h2>
            <p className="text-ink-secondary text-base leading-relaxed">
              {t("landing.partners.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 items-start">
            {pillars.map((p) => (
              <div
                key={p.key}
                className="partner-card rounded-2xl"
                style={{ ["--card-color" as string]: p.color } as React.CSSProperties}
              >
                <div className="relative px-4 pt-4 pb-3 flex items-center gap-3">
                  <div className="partner-icon-chip w-9 h-9 rounded-lg flex items-center justify-center shrink-0" aria-hidden>
                    {p.icon}
                  </div>
                  <h3 className="font-display text-base font-semibold tracking-tight text-ink-primary">{p.title}</h3>
                </div>
                <div className="partner-card-body relative px-4 py-3">
                  <ul className="space-y-1.5">
                    {p.items.map((item, idx) => (
                      <li key={idx} className="flex gap-2.5 items-start">
                        <span className="partner-check mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" aria-hidden>
                          <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3">
                            <path d="M2.5 6.5l2.5 2.5 4.5-5.5" />
                          </svg>
                        </span>
                        <span className="text-sm text-ink-secondary leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`${BUSINESS_APP_URL}/register/business`}
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold transition shadow-cta"
            >
              {t("landing.partners.ctaPrimary")}
            </a>
            <a
              href={`${BUSINESS_APP_URL}/login`}
              className="inline-flex items-center justify-center px-7 py-3.5 rounded-xl bg-white border border-border-light hover:border-brand-primary/50 text-ink-primary font-semibold transition"
            >
              {t("landing.partners.ctaSecondary")}
            </a>
          </div>
        </div>
      </section>

      {/* About */}
      <section
        id="about"
        className="scroll-mt-16 min-h-[45vh] flex items-center justify-center"
      >
        <div className="max-w-6xl mx-auto px-6 py-6 w-full">
          <div className="grid md:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-center">
            {/* Logo */}
            <a
              href={CORPORATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block group"
              aria-label="SolidLabs"
            >
              <div className="relative flex items-center justify-center px-4 py-6 md:px-6 md:py-8">
                {/* Precision-grid backdrop — no blurry blob */}
                <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
                  <div className="about-dots" />
                  <div className="about-halo" />
                </div>
                <img
                  src={solidLabsLogo}
                  alt="SolidLabs"
                  className="relative w-full max-w-[360px] h-auto brightness-0 invert opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition"
                />
              </div>
            </a>

            {/* Text */}
            <div className="text-center md:text-left">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-accent mb-5">
                {t("landing.about.eyebrow")}
              </p>
              <h2 className="font-display font-light text-3xl md:text-4xl tracking-[-0.02em] leading-[1.15] mb-5 whitespace-pre-line text-gradient-brand">
                {t("landing.about.title")}
              </h2>
              <p className="text-ink-on-dark-muted leading-relaxed mb-8">
                {t("landing.about.description")}
              </p>
              <a
                href={CORPORATE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/[0.10] hover:border-brand-primary/40 text-ink-on-dark font-medium transition"
              >
                solidlabs.site
                <span aria-hidden>→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-surface-dark">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row md:items-start md:justify-between gap-6 text-sm text-ink-on-dark-muted">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src={logo} alt="Spotto" className="h-6 w-6 rounded-full logo-glow" />
              <span className="font-display font-light tracking-tight text-ink-on-dark">Spotto</span>
            </div>
            <p>{t("landing.footer.operator")}</p>
            <p>{t("landing.footer.address")}</p>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-on-dark-muted/80 mb-2">
              {t("landing.footer.contact")}
            </p>
            <a href={`mailto:${t("landing.footer.email")}`} className="hover:text-ink-on-dark transition">
              {t("landing.footer.email")}
            </a>
          </div>

          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-on-dark-muted/80 mb-2">
              {t("landing.footer.legal")}
            </p>
            <div className="flex flex-col gap-1.5">
              <a href="/regulamin-uzytkownika" className="hover:text-ink-on-dark transition">{t("landing.footer.termsUser")}</a>
              <a href="/regulamin-obiektu" className="hover:text-ink-on-dark transition">{t("landing.footer.termsBusiness")}</a>
              <a href="/regulamin-trenera" className="hover:text-ink-on-dark transition">{t("landing.footer.termsTrainer")}</a>
              <a href="/privacy-policy" className="hover:text-ink-on-dark transition">{t("landing.footer.privacyPolicy")}</a>
              <a href="/delete-account" className="hover:text-ink-on-dark transition">{t("landing.footer.deleteAccount")}</a>
            </div>
          </div>
        </div>
        <div className="border-t border-white/[0.04] py-3 text-center text-xs text-ink-on-dark-muted/60">
          © {new Date().getFullYear()} Solid Labs PSA. {t("landing.footer.rights")}
        </div>
      </footer>
    </div>
  );
}
